import * as React from 'react';
import { setInnerHtml } from 'sos-common/js/utils/helpers';
import { TextInput } from 'sos-common/js/components/FormControls/TextInput';
import { RadioButtonGroup } from 'sos-common/js/components/FormControls/RadioButtonGroup';
import { RadioButton } from 'sos-common/js/components/FormControls/RadioButton';
import { SelectBox, SelectBoxOption } from 'sos-common/js/components/FormControls/SelectBox';
import { FormField } from 'sos-common/js/commonTypes/formField.types';
import {
  SPECIALISTSJUKSKOTERSKA_AREAS,
  SpecialistsjukskoterskaArea,
  SectionClass,
  SectionProps,
  SectionState,
  TypeOfAnsokan,
  FieldName,
} from '../PublicForm.types';
import { EpiserverProperty } from 'sos-common/js/utils/episerver.property';
import { MAX_LENGTH_NUM } from '../publicform.validation.service';

export interface ISpecialistsjukskoterskaProps extends SectionProps {
  onChangeSpecialist: (isSpecialist: boolean, isDoubleSpecialist?: boolean) => void;
  typeOfAnsokan: TypeOfAnsokan;
}

export interface ISpecialistsjukskoterskaState extends SectionState {
  showDetails: boolean;
  showOtherArea: boolean;
}

const specialistRequiredFields = [
  FieldName.JagAnsokerOmAtt,
  FieldName.SkrivDinInriktning,
  FieldName.SpecialExamensTitel,
  FieldName.SpecialExamensDatum,
  FieldName.SpecialLangd,
  FieldName.SpecialUtbildningsLand,
];

export default class SpecialistsjukskoterskaSection extends SectionClass<
  ISpecialistsjukskoterskaProps,
  ISpecialistsjukskoterskaState
> {
  constructor(props: ISpecialistsjukskoterskaProps) {
    super(props);

    const isSpecialist = props.typeOfAnsokan === TypeOfAnsokan.Specialistkompetens;
    this.state = {
      showDetails: props.typeOfAnsokan === TypeOfAnsokan.Specialistkompetens,
      showOtherArea: false,
      errors: this.getInitialErrorMap(
        isSpecialist ? specialistRequiredFields : [FieldName.AnsokerSpecialistsjukskoterska]
      ),
    };
  }

  static defaultProps = {
    formLanguage: 'sv',
  } as ISpecialistsjukskoterskaProps;

  get areaSelectBoxOption(): SelectBoxOption[] {
    return SPECIALISTSJUKSKOTERSKA_AREAS.map((area) => {
      return { key: area.toString(), text: this.props.Translate(`specialistsjukskoterska_${area}`) };
    });
  }

  onRadioChanged = (field: FormField<string>) => {
    const errors = new Map(this.state.errors);
    const showDetails = field.value === 'Ja';
    const errorMessage = this.props.Translate('error_mandatory');
    if (showDetails) {
      errors.set(FieldName.JagAnsokerOmAtt, {
        showMessage: this.props.submitButtonIsClicked,
        errorMessage: errorMessage,
      });
      errors.set(FieldName.SpecialExamensTitel, {
        showMessage: this.props.submitButtonIsClicked,
        errorMessage: errorMessage,
      });
      errors.set(FieldName.SpecialExamensDatum, {
        showMessage: this.props.submitButtonIsClicked,
        errorMessage: errorMessage,
      });
      errors.set(FieldName.SpecialLangd, {
        showMessage: this.props.submitButtonIsClicked,
        errorMessage: errorMessage,
      });
      errors.set(FieldName.SpecialUtbildningsLand, {
        showMessage: this.props.submitButtonIsClicked,
        errorMessage: errorMessage,
      });
    } else {
      errors.delete(FieldName.JagAnsokerOmAtt);
      errors.delete(FieldName.SpecialExamensTitel);
      errors.delete(FieldName.SpecialExamensDatum);
      errors.delete(FieldName.SpecialLangd);
      errors.delete(FieldName.SpecialUtbildningsLand);
      errors.delete(FieldName.SkrivDinInriktning);
      errors.delete(FieldName.SpecialDatum);
    }
    this.setState({ showDetails, errors }, () => {
      this.onChangeField(field);
    });
    this.props.onChangeSpecialist(showDetails);
  };

  onSelectBoxChanged = (field: FormField<string>) => {
    const errors = new Map(this.state.errors);
    const showOtherArea = field.value === SpecialistsjukskoterskaArea.AnnanInriktning.toString();
    if (showOtherArea) {
      errors.set(FieldName.SkrivDinInriktning, { showMessage: false, errorMessage: null });
    } else {
      errors.delete(FieldName.SkrivDinInriktning);
    }
    this.setState({ showOtherArea, errors }, () => {
      this.onChangeField(field);
      if (showOtherArea) {
        const annanInriktigningElem = document.getElementById(FieldName.SkrivDinInriktning);
        annanInriktigningElem?.focus();
      }
    });
  };

  public render() {
    const { Translate, openWebAnsokanEpiTexts, isInEditMode, typeOfAnsokan } = this.props;
    const { showDetails, showOtherArea } = this.state;
    return (
      <section>
        <EpiserverProperty propertyName="SpecialistsjukskoterskaHeading" isInEditMode={isInEditMode}>
          <h2>{openWebAnsokanEpiTexts.SpecialistsjukskoterskaHeading}</h2>
        </EpiserverProperty>

        {openWebAnsokanEpiTexts.SpecialistsjukskoterskaPreamble && (
          <EpiserverProperty
            propertyName="SpecialistsjukskoterskaPreamble"
            wrappingElement="span"
            isInEditMode={isInEditMode}>
            <p className="preamble">{`${openWebAnsokanEpiTexts.SpecialistsjukskoterskaPreamble} ${Translate(
              'mandatory'
            )}`}</p>
          </EpiserverProperty>
        )}
        <EpiserverProperty
          propertyName="SpecialistsjukskoterskaBody"
          usingDangerouslySetInnerHTML={true}
          isInEditMode={isInEditMode}>
          <div dangerouslySetInnerHTML={setInnerHtml(openWebAnsokanEpiTexts.SpecialistsjukskoterskaBody)} />
        </EpiserverProperty>

        {typeOfAnsokan === TypeOfAnsokan.Legitimation && (
          <RadioButtonGroup
            errorMessage={this.getErrorMessage(FieldName.AnsokerSpecialistsjukskoterska)}
            onChange={this.onRadioChanged}
            fieldName={FieldName.AnsokerSpecialistsjukskoterska}>
            <RadioButton value="Ja" label={Translate('ja')} />
            <RadioButton value="Nej" label={Translate('nej')} />
          </RadioButtonGroup>
        )}
        {showDetails && (
          <div>
            <div className="row m-b-2 m-t-2">
              <div
                className={`col-sm-6 ${this.getErrorSiblingClass(FieldName.SkrivDinInriktning, [
                  FieldName.JagAnsokerOmAtt,
                ])}`}>
                <SelectBox
                  label={Translate(FieldName.JagAnsokerOmAtt) + ' (obligatoriskt)'}
                  fieldName={FieldName.JagAnsokerOmAtt}
                  defaultOptionText={Translate('valj')}
                  options={this.areaSelectBoxOption}
                  onChange={this.onSelectBoxChanged}
                  isRequired={true}
                  errorMessage={this.getErrorMessage(FieldName.JagAnsokerOmAtt)}
                  className={'select-full-width'}
                />
              </div>
              {showOtherArea && (
                <div
                  className={`col-sm-6 ${this.getErrorSiblingClass(FieldName.JagAnsokerOmAtt, [
                    FieldName.SkrivDinInriktning,
                  ])}`}>
                  <TextInput
                    id={FieldName.SkrivDinInriktning}
                    label={Translate(FieldName.SkrivDinInriktning) + ' (obligatoriskt)'}
                    isRequired={true}
                    fieldName={FieldName.SkrivDinInriktning}
                    errorMessage={this.getErrorMessage(FieldName.SkrivDinInriktning)}
                    onChange={this.onChangeField}
                    maxLength={MAX_LENGTH_NUM}
                    showMaxLengthMessage={true}
                    maxLengthMessageLanguage={this.props.formLanguage}
                  />
                </div>
              )}
            </div>

            <div className="row m-b-2">
              <div
                className={`col-sm-6 ${this.getErrorSiblingClass(FieldName.SpecialExamensDatum, [
                  FieldName.SpecialExamensTitel,
                ])}`}>
                <TextInput
                  label={Translate(FieldName.SpecialExamensTitel) + ' (obligatoriskt)'}
                  isRequired={true}
                  fieldName={FieldName.SpecialExamensTitel}
                  errorMessage={this.getErrorMessage(FieldName.SpecialExamensTitel)}
                  onChange={this.onChangeField}
                  maxLength={MAX_LENGTH_NUM}
                  showMaxLengthMessage={true}
                  maxLengthMessageLanguage={this.props.formLanguage}
                />
              </div>
              <div
                className={`col-sm-6 ${this.getErrorSiblingClass(FieldName.SpecialExamensTitel, [
                  FieldName.SpecialExamensDatum,
                ])}`}>
                <TextInput
                  label={Translate(FieldName.SpecialExamensDatum) + ' (obligatoriskt)'}
                  isRequired={true}
                  fieldName={FieldName.SpecialExamensDatum}
                  errorMessage={this.getErrorMessage(FieldName.SpecialExamensDatum)}
                  onChange={this.onChangeField}
                  maxLength={MAX_LENGTH_NUM}
                  showMaxLengthMessage={true}
                  maxLengthMessageLanguage={this.props.formLanguage}
                />
              </div>
            </div>
            <div className="row m-b-2">
              <div
                className={`col-sm-6 ${this.getErrorSiblingClass(FieldName.SpecialLangd, [FieldName.SpecialDatum])}`}>
                <TextInput
                  errorMessage={this.getErrorMessage(FieldName.SpecialDatum)}
                  label={Translate(FieldName.SpecialDatum)}
                  fieldName={FieldName.SpecialDatum}
                  onChange={this.onChangeField}
                  maxLength={MAX_LENGTH_NUM}
                  showMaxLengthMessage={true}
                  maxLengthMessageLanguage={this.props.formLanguage}
                />
              </div>
              <div
                className={`col-sm-6 ${this.getErrorSiblingClass(FieldName.SpecialDatum, [FieldName.SpecialLangd])}`}>
                <TextInput
                  errorMessage={this.getErrorMessage(FieldName.SpecialLangd)}
                  label={Translate(FieldName.SpecialLangd) + ' (obligatoriskt)'}
                  isRequired={true}
                  fieldName={FieldName.SpecialLangd}
                  onChange={this.onChangeField}
                  maxLength={MAX_LENGTH_NUM}
                  showMaxLengthMessage={true}
                  maxLengthMessageLanguage={this.props.formLanguage}
                />
              </div>
            </div>
            <div className="row m-b-2">
              <div className="col-sm-6">
                <TextInput
                  label={Translate(FieldName.SpecialUtbildningsLand) + ' (obligatoriskt)'}
                  isRequired={true}
                  fieldName={FieldName.SpecialUtbildningsLand}
                  errorMessage={this.getErrorMessage(FieldName.SpecialUtbildningsLand)}
                  onChange={this.onChangeField}
                  maxLength={MAX_LENGTH_NUM}
                  showMaxLengthMessage={true}
                  maxLengthMessageLanguage={this.props.formLanguage}
                  autoComplete="country-name"
                />
              </div>
            </div>
          </div>
        )}

        <hr className="formdivider" />
      </section>
    );
  }
}
